import CIcon from '@ant-design/icons/lib/components/Icon';
import { cx } from '@emotion/css';
import styled from '@emotion/styled';
import { Button as AButton, ButtonProps } from 'antd';
import { withErrorBoundary } from 'components/ErrorBoundary/withErrorBoundary';
import FormatNumber from 'components/FormatNumber';
import withSuspense from 'components/withSuspense';
import { useFlagrDashboardRankValue } from 'hooks/business/system/useFlagr';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import React, { PropsWithChildren, useMemo } from 'react';
import { useTranslate, TFnType } from '@tolgee/react';
import { ReactComponent as PkSvg } from './pk.svg';

interface AheadButtonProps extends Omit<ButtonProps, 'children'> {
  rate: number;
  avg?: number;
  formatter?: (rate: number, avg: number, t: TFnType) => React.ReactNode;
}
const Text = styled.span``;
const Button = styled(AButton)`
  display: flex;
  align-items: center;
  padding: 0 4px 0 0;
  font-size: 12px;
  height: 26px;
  line-height: 26px;
  border-radius: 4px;
  &.ant-btn-primary {
    color: var(--ant-error-color);
    border: 1px solid var(--ant-error-color);
    background: #fff;
    .anticon + span {
      margin-left: 4px;
      display: flex;
    }

    &.ahead {
      color: var(--ant-primary-color);
      border-color: var(--ant-primary-color);
      .prefix {
        background: var(--ant-primary-color);
      }
    }
    &.wrap {
      ${Text} {
        display: block;
        line-height: 1;
        text-align: left;
      }
    }
  }
`;
const Icon = styled(CIcon)`
  display: flex;
  color: #fff;
  justify-content: center;
  align-items: center;
  background: var(--ant-error-color);
  padding: 2px 2px 2px 3px;
  height: 100%;
  font-size: 20px;
`;

export const DEFAULT_BENCHMARK = 0.5;

export const calcAhead = (rate: number, avg = DEFAULT_BENCHMARK) => rate >= avg;

const defaultFormatter: AheadButtonProps['formatter'] = (rate, _, t) => {
  // 这里暂时采取字符串拼接的方式 原因是echarts渲染内容与本组件不一致 目前中英文没有问题
  // @example Wastage.tsx:443 renderAhead
  const aheadText = t('ViewDashboard.领先');
  const postfixText = t('ViewDashboard.同行');
  return (
    <Text>
      <div>
        {aheadText}
        {rate < 0 ? '-' : <FormatNumber number={rate} format="0%" />}
      </div>
      <div>{postfixText}</div>
    </Text>
  );
};

function AheadButton(props: PropsWithChildren<AheadButtonProps>) {
  const {
    rate,
    avg = DEFAULT_BENCHMARK,
    formatter = defaultFormatter,
    className,
    ...otherProps
  } = props;
  const flag = useFlagrDashboardRankValue();
  const isAhead = calcAhead(rate, avg);
  const { t } = useTranslate();
  const titleText = t('review.点击查看');
  const language = useSystemLanguageValue();
  const isWrap = language !== 'zh-CN';
  //  是否换行  在除开cn的环境下不换行可能会出现宽度溢出
  const content = useMemo(() => formatter?.(rate, avg, t), [rate, avg, formatter, t]);
  return flag?.allowed ? (
    <Button
      className={cx(className, { ahead: isAhead, wrap: isWrap })}
      type="primary"
      icon={<Icon className="prefix" component={PkSvg} />}
      {...otherProps}
      title={titleText}
    >
      {content}
    </Button>
  ) : null;
}

export default withErrorBoundary(withSuspense(AheadButton, { fallback: null }), { fallback: null });
